import { useMemo } from "react";
import { useAppSelector } from "@/hooks";
import { checkServiceAvailability } from "@/utils/weekDays";
import { useFetchBranchQuery } from "@/features/organizations/api/branchApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { LocalStorage } from "@/constants/localStorage";

const useServiceFee = () => {
    const selectedService = useAppSelector((state) => state.order.flow);
    const organizationId = useAppSelector((store) => store.ui.activeOrganization);
    const currentOrganization = useAppSelector((store) => store.organizations[organizationId] ?? null);
    const deliveryLocation = useAppSelector((state) => state.order.deliveryLocation);

    const organizationBranch = useFetchBranchQuery(organizationId ?? skipToken, {
        skip: !organizationId,
    });
    const branches = organizationBranch?.data && Array.isArray(organizationBranch.data) ? organizationBranch.data : [];

    const activeBranchId = useAppSelector((state) => state.ui.activeBranch);
    const branchIdToUse = useAppSelector((state) => state.order.branch?.id) || activeBranchId;

    const serviceConfig = useMemo(() => {
        if (!selectedService || !currentOrganization?.settings) return null
        return currentOrganization.settings.services?.[selectedService];
    }, [selectedService, currentOrganization?.settings?.services]);

    const serviceType = serviceConfig?.type;

    const baseFee = Number(serviceConfig?.fee?.baseRate) || 0;

    const feePerDistance = Number(serviceConfig?.fee?.valuePerDistance) || null;

    const isAvailable = useMemo(() => {
        const lsBranchId = localStorage.getItem(LocalStorage.BRANCH_ID);
        const activeBranchIdToUse = branchIdToUse || lsBranchId;
        
        const branchToUse = branches?.find((b) => b.id === activeBranchIdToUse);

        if (branchToUse && selectedService) {
            const hasService = branchToUse.services?.includes(selectedService);
            const isBranchOpen = checkServiceAvailability({
                isEnabled: true,
                schedule: branchToUse.schedule,
            });
            return hasService && isBranchOpen;
        }

        return checkServiceAvailability(serviceConfig);
    }, [serviceConfig, branches, branchIdToUse, selectedService]);

    const fee = useMemo(() => {
        const finalDistance = ((deliveryLocation?.distance?.number ?? 1) / 1000) || 1; // Distance is return al meters

        if (!isAvailable) return 0;

        if (serviceType === "fixed") return Number(serviceConfig?.fee?.value) || 0;

        if (serviceType === "auto" &&
            feePerDistance &&
            finalDistance
        ) {
            const distanceFee = Math.floor(feePerDistance * finalDistance);
            return distanceFee >= baseFee ? distanceFee : baseFee;
        }
        
        return 0;
    }, [
        isAvailable,
        serviceType,
        baseFee,
        feePerDistance,
        deliveryLocation?.distance?.number,              
    ]);

    
    
    return {
        fee,
        isAvailable,
        isEnabled: serviceConfig?.isEnabled ?? false,
        baseFee,
        feePerDistance,
        type: serviceType,
        mode: selectedService,
    }
};

export default useServiceFee;

